// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what happened to a record, and what it used to say
//  ---------------------------------------------------------------------------
//  Two stores, one subject, and they are deliberately on opposite sides of the
//  wire:
//
//    THE LOG   `md:history:<record>:<device>`  — small, synced, one per device
//    THE BODY  IndexedDB `mandana-versions`    — large, device-local, never sent
//
//  ── WHY THE LOG'S KEY CARRIES THE DEVICE ───────────────────────────────────
//  The obvious key is `history:<record>`, capped. It has TWO WRITERS, and this
//  app syncs: two devices each appending while offline is the ordinary case,
//  and last-write-wins on a whole document throws one of the two logs away.
//  Silently, and specifically the log of whichever device synced first — which
//  is the one that had been away and has the most to say.
//
//  There is no merge rule that rescues it without inventing a per-entry
//  identity, a union, and a pruning race between two devices trimming the same
//  cap from different ends. Putting the device in the KEY removes the problem
//  rather than solving it: one key, one writer, for ever. LWW is then safe BY
//  CONSTRUCTION, the reader unions, and nothing has to stay honest.
//  md-membership.ts reached the same shape for the same reason.
//
//  THE CAP IS PER DEVICE, and that is the point of it being where it is: a
//  device trimming its own log cannot take an entry off another device's.
//
//  ── WHY THE BODIES ARE NOT IN `md:` ────────────────────────────────────────
//  A version is somebody's essay as it stood before a change. localStorage
//  gives the whole origin about 5 MB, and this plan exists partly to make that
//  wall visible — spending it on the feature that reveals it would be a joke
//  with a straight face. Bodies go to IndexedDB, beside the drawings that left
//  for the same reason (md-boarddb.js).
//
//  AND THEY NEVER SYNC. A version is what THIS device saw before a change
//  arrived, which is a statement about this device — snapshotting on the way in
//  is what makes it meaningful (4.2). Sending bodies would multiply every essay
//  by the number of devices for no recoverable information.
//
//  ── AND THE TWO MEET IN ONE PLACE ──────────────────────────────────────────
//  md-history.ts writes the log, because it is already the only thing that
//  knows what one GESTURE was (4.1). A line names the fields that moved and
//  carries the value each one held — inline while it is small, and otherwise as
//  a pointer to a version, which is a body and therefore stays here. That
//  ceiling is MD_HISTORY_INLINE_MAX below, beside the log's other one: both
//  answer the same question, which is how big a synced document may get.
//
//  So this file owns the reading BOTH halves need — which documents hold
//  somebody's writing, and what a record says inside one — rather than leaving
//  the log and the snapshot to arrive at it separately and disagree.
//
//  ── THE THREE PROMISES, INHERITED ──────────────────────────────────────────
//  From md-storage.js and md-boarddb.js, unchanged: it never throws, it always
//  reports, and every fault is recorded. A refused put answers '' and a refused
//  list answers [], because the caller is a surface and "no versions" is a
//  sentence it can show.
// ════════════════════════════════════════════════════════════════════════════

/** One entry in a record's log: a moment, and what happened at it. */
interface MdHistoryLogEntry {
  at: number;
  [field: string]: unknown;
}

(function (g: any) {
  'use strict';

  /* ── the log ──────────────────────────────────────────────────────────── */

  const MD_HISTORY_PREFIX = 'md:history:';

  /* Per DEVICE, so trimming is a device trimming its own. Generous, because an
     entry is a line — a moment, a verb and a field name — and the bodies that
     would make it heavy live in the other store. */
  const MD_HISTORY_LOG_MAX = 200;

  /* …and how long a value may be before it stops riding in the entry (4.1).
     The two numbers answer one question together — how big may a log get? —
     which is why they live side by side rather than one of them next to its
     caller.

     TWO HUNDRED CHARACTERS, and the reasoning is the shape of the data rather
     than a round number. A record's title is capped at sixty (md-record.ts), a
     status is a word, a date is ten characters: everything a person changes
     BY PICKING is comfortably inside, so those entries stay whole and readable
     with no second lookup. Prose passes it almost immediately, which is the
     point — a body belongs in the version store, not in a document that syncs.

     The worst case is what fixes it. Two hundred entries, each naming a couple
     of fields at this ceiling, is a log around 80 KB. That is a lot for one
     record and it is survivable on a 5 MB origin; the same log with an essay
     in every entry is not, and that is the failure this ceiling exists to
     prevent — one person writing at length making every device's storage
     their problem. */
  const MD_HISTORY_INLINE_MAX = 200;

  const flat = (ref: unknown): string => {
    const parsed = typeof ref === 'string' && g.mdRecordRefParse
      ? g.mdRecordRefParse(ref) : null;
    return parsed ? `${parsed.type}:${parsed.id}` : '';
  };

  /* The device segment must not contain a colon, or the key stops parsing back
     into its two halves. mdDeviceId mints from a fixed alphabet, so this is a
     guard against a caller passing something else rather than against the id. */
  /* Lowercase alphanumeric, and in step with server/sync.js's KEY_PATTERN: a
     device segment this side accepts and the server refuses is a log that is
     written locally for ever and never travels — which looks exactly like
     working. mdDeviceId mints lowercase hex, well inside it. */
  const DEVICE_RE = /^[a-z0-9]{1,64}$/;

  /** Where one device's log for one record is kept. '' when it would not name
      one, which is how every caller here refuses without a throw.

      OMITTING the device means "this one" and asks md-device.ts. PASSING an
      empty one is a caller that thought it had a device and did not, and
      answering it with this device's log would file somebody else's entries
      under our name — quietly, and for ever, because a key has one writer. */
  function mdHistoryLogKey(ref: unknown, device?: unknown): string {
    const record = flat(ref);
    const dev = device === undefined
      ? (typeof g.mdDeviceId === 'function' ? g.mdDeviceId() : '')
      : device;
    if (!record || typeof dev !== 'string' || !DEVICE_RE.test(dev)) return '';
    return `${MD_HISTORY_PREFIX}${record}:${dev}`;
  }

  function entryOk(value: unknown): value is MdHistoryLogEntry {
    return !!value && typeof value === 'object' && !Array.isArray(value)
      && typeof (value as MdHistoryLogEntry).at === 'number'
      && Number.isFinite((value as MdHistoryLogEntry).at);
  }

  /** Add one line to THIS device's log for a record. */
  function mdHistoryLogAppend(ref: unknown, entry: unknown, device?: unknown): boolean {
    const key = mdHistoryLogKey(ref, device);
    if (!key || !entryOk(entry) || !g.mdRead || !g.mdWrite) return false;
    const held = g.mdRead(key, []);
    const rows = (Array.isArray(held) ? held : []).filter(entryOk);
    rows.push(entry);
    /* The OLDEST goes. A log that dropped its newest entries would answer
       "nothing has happened here lately", which is the opposite of true. */
    const trimmed = rows.length > MD_HISTORY_LOG_MAX
      ? rows.slice(rows.length - MD_HISTORY_LOG_MAX) : rows;
    return !!g.mdWrite(key, trimmed);
  }

  /** Every device's log for one record, unioned and in time order.

      No merge, no dedupe, no reconciliation: each key had exactly one writer,
      so every entry present is an entry that happened. Ties break on the device
      segment so two devices reading the same set list it identically. */
  function mdHistoryLogRead(ref: unknown): MdHistoryLogEntry[] {
    const record = flat(ref);
    if (!record || !g.mdStorageKeys || !g.mdRead) return [];
    const prefix = `${MD_HISTORY_PREFIX}${record}:`;
    const out: { entry: MdHistoryLogEntry; dev: string }[] = [];
    for (const key of g.mdStorageKeys(prefix)) {
      const dev = key.slice(prefix.length);
      /* A key with a colon left in it names a longer ref, not this record —
         `history:note:aa:dev` and `history:note:aa:bb:dev` share a prefix. */
      if (!DEVICE_RE.test(dev)) continue;
      const held = g.mdRead(key, []);
      if (!Array.isArray(held)) continue;
      for (const entry of held) if (entryOk(entry)) out.push({ entry, dev });
    }
    out.sort((a, b) => (a.entry.at !== b.entry.at ? a.entry.at - b.entry.at
      : a.dev < b.dev ? -1 : a.dev > b.dev ? 1 : 0));
    return out.map((row) => row.entry);
  }

  /** Every log for one record, gone. Used when a deletion's buffer runs out. */
  function mdHistoryLogRemoveFor(ref: unknown): number {
    const record = flat(ref);
    if (!record || !g.mdStorageKeys || !g.mdRemove) return 0;
    const prefix = `${MD_HISTORY_PREFIX}${record}:`;
    let gone = 0;
    for (const key of g.mdStorageKeys(prefix)) {
      if (!DEVICE_RE.test(key.slice(prefix.length))) continue;
      if (g.mdRemove(key)) gone += 1;
    }
    return gone;
  }

  /* ── the wire ─────────────────────────────────────────────────────────────
     THE LOG SYNCS. THE BODIES DO NOT, and that asymmetry is the story: a log
     entry is a line, and a version is somebody's essay.

     A family is four changes — the grammar in server/sync.js, the offer, the
     sanitiser and the applier — and this file holds three of them. But the
     offer is narrower here than in any other family, because the whole reason
     the device is in the key is that a key has exactly ONE writer. A rule that
     held only in local storage would be a convention; holding it on the wire
     is what makes it a property.

     So a device offers ITS OWN log and no other. The logs it has pulled are
     HELD — mdHeldKeys, the same rail that protects a board too big to push —
     because absence from the offer is how this app says "deleted", and without
     the hold the first sync after a pull would tell every other device to
     delete its own history. */

  const SYNC_PREFIX = 'history:';

  const mine = (): string => (typeof g.mdDeviceId === 'function' ? g.mdDeviceId() : '');

  /** The sync key for a stored one, or '' if the stored key is not one of ours. */
  function syncKeyOf(stored: string): string {
    return stored.startsWith(MD_HISTORY_PREFIX)
      ? `${SYNC_PREFIX}${stored.slice(MD_HISTORY_PREFIX.length)}` : '';
  }

  /** Split a sync key into the record and the device that wrote it. */
  function partsOf(key: unknown): { record: string; device: string } | null {
    if (typeof key !== 'string' || !key.startsWith(SYNC_PREFIX)) return null;
    const rest = key.slice(SYNC_PREFIX.length);
    const cut = rest.lastIndexOf(':');
    if (cut <= 0) return null;
    const record = flat(rest.slice(0, cut));
    const device = rest.slice(cut + 1);
    return record && DEVICE_RE.test(device) ? { record, device } : null;
  }

  /** This device's logs, and only this device's. */
  function mdHistoryLogSyncDocs(): Record<string, MdHistoryLogEntry[]> {
    const out: Record<string, MdHistoryLogEntry[]> = {};
    const me = mine();
    if (!me || !g.mdStorageKeys || !g.mdRead) return out;
    for (const stored of g.mdStorageKeys(MD_HISTORY_PREFIX)) {
      if (!stored.endsWith(`:${me}`)) continue;
      const sync = syncKeyOf(stored);
      if (!sync || !partsOf(sync)) continue;
      const held = g.mdRead(stored, []);
      if (Array.isArray(held)) out[sync] = held.filter(entryOk);
    }
    return out;
  }

  /** The logs this device holds but did not write. Never offered, and never
      mourned — see the header above. */
  function mdHistoryLogHeldKeys(): string[] {
    const me = mine();
    if (!g.mdStorageKeys) return [];
    const out: string[] = [];
    for (const stored of g.mdStorageKeys(MD_HISTORY_PREFIX)) {
      if (me && stored.endsWith(`:${me}`)) continue;
      const sync = syncKeyOf(stored);
      if (sync && partsOf(sync)) out.push(sync);
    }
    return out;
  }

  /** …and yes, take the incoming copy, even though this key is held.

      md-sync.js refuses a document that looks locally dirty unless the family
      that owns it says it reconciles — and a held key reads as dirty, because
      held means "not offered" and the pull cannot tell "not offered because I
      have newer work" from "not offered because it was never mine".

      A foreign log is the second, always. This device is not that key's writer
      and never will be, so there is no local writing of it to protect, and
      the applier below is a replacement for exactly the same reason. Without
      this the first version of another device's log arrived — nothing local
      yet, so nothing held — and every version after it was declined for ever.
      Which looks precisely like the other device never editing anything, and
      is the one failure a history feature cannot have. */
  function mdHistoryLogMergesDoc(key: unknown): boolean {
    const parts = partsOf(key);
    return !!parts && parts.device !== mine();
  }

  /** An arriving log. Entries with no moment are dropped INDIVIDUALLY rather
      than taking the log with them: one unorderable line is a line, and the
      rest of somebody's history is worth keeping. */
  function mdHistoryLogSanitiseSyncDoc(key: unknown, value: unknown):
  MdHistoryLogEntry[] | undefined {
    if (!partsOf(key) || !Array.isArray(value)) return undefined;
    return value.filter(entryOk).slice(-MD_HISTORY_LOG_MAX);
  }

  /** …and land it, whole. This is the ONE place a device writes a log it does
      not own, and it is a replacement rather than a merge — correctly, because
      the far side is that key's only writer and its copy is the whole truth. */
  function mdHistoryLogApplyDoc(key: unknown, value: unknown): boolean {
    const parts = partsOf(key);
    if (!parts || !g.mdWrite || !g.mdRemove) return false;
    const stored = mdHistoryLogKey(parts.record, parts.device);
    if (!stored) return false;
    if (value == null) return !!g.mdRemove(stored);
    const clean = mdHistoryLogSanitiseSyncDoc(key, value);
    return clean ? !!g.mdWrite(stored, clean) : false;
  }

  /* ── where somebody's writing actually lives ──────────────────────────────
     Both halves of Phase 4 need the same reading and must not each invent it:
     the log has to NAME the record a gesture changed (4.1), and the snapshot
     has to know what this device is about to stop being able to say (4.2).

     A record has TWO HOMES, and md-record.ts's header says why. A day record is
     an element inside `md:records:<day>` — a document that merges and tombstones
     as a unit — while a project, a pursuit and a domain have no day, so each
     keeps its writing in a document of its own. Those four are the whole list.

     `md:` + THE SYNC KEY IS THE STORAGE KEY for all four. That is not a
     coincidence to lean on quietly, it is the reason the snapshot can be handed
     a document arriving from the server and find the one it is about to
     replace. It is also the only reason this file spells another module's key
     out loud: asking md-project.ts and md-domain.ts would make the reading
     depend on three modules being present, and a snapshot that silently does
     not happen is indistinguishable from one that had nothing to keep. */

  const DAY_DOC = /^md:(?:events|records):\d{4}-\d{2}-\d{2}$/;
  const HOME_DOC = /^md:(project|pursuit|domain):([a-z0-9]{1,64})$/;

  /** Does this storage key name a document that holds somebody's writing?

      Asked before a caller parses anything, deliberately: a gesture on a
      whiteboard must not cost a JSON.parse of the drawing to discover there is
      no record inside it. */
  function mdRecordDocIsHome(storageKey: unknown): boolean {
    return typeof storageKey === 'string'
      && (DAY_DOC.test(storageKey) || HOME_DOC.test(storageKey));
  }

  /** Every record one document holds, as flat ref → the row it holds. Empty for
      a document that holds no writing, which is most of them. */
  function mdRecordDocRows(storageKey: unknown, value: unknown):
  Record<string, Record<string, unknown>> {
    const out: Record<string, Record<string, unknown>> = {};
    if (typeof storageKey !== 'string') return out;

    const home = HOME_DOC.exec(storageKey);
    if (home) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
      const ref = flat(`${home[1]}:${home[2]}`);
      if (ref) out[ref] = value as Record<string, unknown>;
      return out;
    }

    if (!DAY_DOC.test(storageKey) || !Array.isArray(value)) return out;
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
      const r = row as Record<string, unknown>;
      /* A row with no usable uid names no record. Not an error — md-merge.ts
         drops the same rows for the same reason — and a line filed under a
         guessed ref would be worse than no line at all. */
      if (typeof r.uid !== 'string' || !r.uid) continue;
      const type = typeof g.mdRecordType === 'function' ? g.mdRecordType(r) : 'note';
      const ref = flat(`${type}:${r.uid}`);
      if (ref) out[ref] = r;
    }
    return out;
  }

  /** The writing in one row — the single text the editor puts on the screen.

      A home document keeps it in `text`; a day record keeps a title and a body
      and md-record.ts composes the two, de-duplicating the era when the title
      was repeated as the body's first line. ONE STRING EITHER WAY, which is
      what makes a version of a project and a version of a note the same kind of
      thing to list, to show and to restore. */
  function mdRecordDocText(row: unknown): string {
    if (!row || typeof row !== 'object') return '';
    const text = (row as { text?: unknown }).text;
    if (typeof text === 'string') return text;
    return typeof g.mdRecordSeedText === 'function' ? String(g.mdRecordSeedText(row) || '') : '';
  }

  /* ── the bodies ───────────────────────────────────────────────────────── */

  const DB_NAME = 'mandana-versions';
  const DB_VERSION = 1;
  const STORE = 'versions';

  /* Resolved once and held, exactly as md-boarddb.js does — a name that could
     change mid-session is a device losing track of its own history halfway
     through reading it. Called defensively: md-adopt.ts loads first in the app
     but not in every harness, and the bare name is the identity default. */
  let dbName: string | null = null;
  function nameOf(): string {
    if (dbName !== null) return dbName;
    const resolved: string = (typeof g.mdCompartmentDbName === 'function')
      ? String(g.mdCompartmentDbName(DB_NAME) || DB_NAME) : DB_NAME;
    dbName = resolved;
    return resolved;
  }

  const health = { lastOk: 0, lastFault: null as any, faults: 0, writes: 0 };

  function isQuota(err: any): boolean {
    if (!err) return false;
    const name = err.name || '';
    return name === 'QuotaExceededError'
      || name === 'NS_ERROR_DOM_QUOTA_REACHED'
      || err.code === 22 || err.code === 1014;
  }

  function fault(op: string, key: string | null, err: any) {
    health.lastFault = {
      op, key, at: Date.now(), quota: isQuota(err),
      name: (err && err.name) || 'Error',
      message: (err && err.message) || String(err),
    };
    health.faults += 1;
    return health.lastFault;
  }

  function mdVersionHealth() { return { ...health }; }
  function mdVersionsAvailable(): boolean { return !!g.indexedDB; }

  let dbPromise: Promise<any> | null = null;

  function open(): Promise<any> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      if (!g.indexedDB) { dbPromise = null; reject(new Error('no indexeddb')); return; }
      const req = g.indexedDB.open(nameOf(), DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => {
        const db = req.result;
        /* Both drops check the cache is still theirs, so a late close cannot
           discard a connection a later call already opened. */
        const mine = dbPromise;
        db.onversionchange = () => {
          try { db.close(); } catch (e) { /* closing */ }
          if (dbPromise === mine) dbPromise = null;
        };
        /* The browser closed it under a suspended tab. Kept past this, every
           later request fails on a handle nothing can revive. */
        db.onclose = () => { if (dbPromise === mine) dbPromise = null; };
        resolve(db);
      };
      req.onerror = () => { dbPromise = null; reject(req.error || new Error('indexeddb open failed')); };
      req.onblocked = () => { dbPromise = null; reject(new Error('indexeddb blocked')); };
    });
    return dbPromise;
  }

  function store(db: any, mode: string) {
    try {
      return db.transaction(STORE, mode).objectStore(STORE);
    } catch (err: any) {
      /* A finished connection over a healthy database: drop it so the next
         call opens a live one. This call still fails, and says so. */
      if (err && err.name === 'InvalidStateError' && dbPromise) dbPromise = null;
      throw err;
    }
  }

  function wrap<T>(req: any): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('indexeddb request failed'));
    });
  }

  /* A version's key names the record it belongs to, so listing one record's
     versions is a prefix scan rather than a walk of every version on the
     device — and so a deletion's sweep can remove them all without an index. */
  const versionKey = (record: string, at: number, n: number): string =>
    `${record}|${String(at).padStart(15, '0')}|${n}`;

  let seq = 0;

  /* The `at` is what orders the list, and it is the caller's: a snapshot taken
     immediately before an incoming change is applied is stamped with that
     change's moment, not with when the write happened to land. */
  function mint(record: string, meta: any): { key: string; at: number } {
    const at = meta && Number.isFinite(Number(meta.at)) ? Number(meta.at) : Date.now();
    seq += 1;
    return { key: versionKey(record, at, seq), at };
  }

  /* One row, one shape, whichever door asked for it. Never throws — see the
     three promises in the header. */
  async function land(key: string, record: string, at: number, text: string, meta: any):
  Promise<boolean> {
    try {
      const db = await open();
      await wrap(store(db, 'readwrite').put({
        ref: record, at, bytes: text.length, body: text,
        ...(meta && typeof meta.what === 'string' ? { what: meta.what } : {}),
      }, key));
      health.lastOk = Date.now();
      health.writes += 1;
      return true;
    } catch (err) {
      fault('put', key, err);
      return false;
    }
  }

  /** Keep one body. Answers the key it was stored under, or '' if it was not. */
  async function mdVersionPut(ref: unknown, body: unknown, meta?: any): Promise<string> {
    const record = flat(ref);
    if (!record) return '';
    const text = typeof body === 'string' ? body : '';
    const { key, at } = mint(record, meta);
    return (await land(key, record, at, text, meta)) ? key : '';
  }

  /** The same thing for a caller that cannot wait: answers the key the body is
      going under NOW, and stores it behind that.

      THE ONE CALLER IS md-history.ts, and the constraint is real. A step seals
      inside a microtask after somebody's gesture, and it cannot await a
      database — a log line written a turn later is a log line a closed tab
      loses, which is the entire failure 4.1 exists to end.

      A POINTER WHOSE BODY NEVER LANDED IS NOT A NEW STATE. The log syncs and
      the bodies do not, so every device that pulls this log already holds
      entries pointing at versions it has never had and never will. A reader has
      to answer "the body is not here" on the ordinary path, and does —
      mdVersionGet answers null rather than throwing. A refused write here joins
      that case rather than opening one. */
  function mdVersionKeep(ref: unknown, body: unknown, meta?: any): string {
    const record = flat(ref);
    if (!record || typeof body !== 'string' || !body) return '';
    const { key, at } = mint(record, meta);
    void land(key, record, at, body, meta);
    return key;
  }

  /** One body, whole. */
  async function mdVersionGet(key: unknown): Promise<any | null> {
    if (typeof key !== 'string' || !key) return null;
    try {
      const db = await open();
      const value = await wrap<any>(store(db, 'readonly').get(key));
      return value === undefined ? null : value;
    } catch (err) {
      fault('get', key, err);
      return null;
    }
  }

  /** What versions exist for a record, newest first, WITHOUT their bodies.

      A list is for choosing from. Handing back the essays would put every
      version of a long record in memory to draw a row of dates. */
  async function mdVersionList(ref: unknown): Promise<any[]> {
    const record = flat(ref);
    if (!record) return [];
    try {
      const db = await open();
      const keys = await wrap<string[]>(store(db, 'readonly').getAllKeys());
      const mine = (keys || []).filter((k) => typeof k === 'string' && k.startsWith(`${record}|`));
      const rows: any[] = [];
      for (const key of mine) {
        const value = await wrap<any>(store(db, 'readonly').get(key));
        if (!value) continue;
        const { body: _body, ...rest } = value;
        rows.push({ key, ...rest });
      }
      rows.sort((a, b) => (b.at - a.at) || (a.key < b.key ? 1 : -1));
      return rows;
    } catch (err) {
      fault('list', record, err);
      return [];
    }
  }

  /** Every version of one record, gone. Answers how many went. */
  async function mdVersionRemoveFor(ref: unknown): Promise<number> {
    const record = flat(ref);
    if (!record) return 0;
    try {
      const db = await open();
      const keys = await wrap<string[]>(store(db, 'readonly').getAllKeys());
      const mine = (keys || []).filter((k) => typeof k === 'string' && k.startsWith(`${record}|`));
      for (const key of mine) await wrap(store(db, 'readwrite').delete(key));
      return mine.length;
    } catch (err) {
      fault('remove', record, err);
      return 0;
    }
  }

  /* ── the moment a version is worth taking ─────────────────────────────────
     IMMEDIATELY BEFORE AN INCOMING CHANGE IS APPLIED, and nowhere else.

     That single instruction is what makes a device-local version meaningful
     without a body ever crossing the wire. The device that SAW the old text is
     the device that keeps it — so "what did this say before my laptop changed
     it?" has an answer here, on the machine that was looking at it, and no
     essay is multiplied by the number of devices to get one.

     A MOMENT LATER THERE IS NOTHING TO TAKE. The apply replaces the document,
     and what it replaced is gone from every store on the device. An ordering
     that is only a convention is an ordering somebody reverses while tidying,
     so tests/versions-device-local.test.js asserts the late call keeps nothing.

     md-sync.js's mdApplyDoc is the one funnel every pulled document passes
     through — every family, present and future — which is why the call belongs
     in front of it rather than in each applier. */

  /** The moment the old text stopped being true: the moment of the change that
      displaced it, not the moment this device got round to applying it. Two
      devices pulling the same change then list the version at the same time. */
  function displacedAt(row: unknown): number {
    const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
    for (const field of ['updatedAt', 'at']) {
      const n = Number(r[field]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return Date.now();
  }

  /** Keep what this device is about to lose. Answers the keys it kept. */
  async function mdVersionSnapshotIncoming(key: unknown, value: unknown): Promise<string[]> {
    if (typeof key !== 'string' || !key || typeof g.mdRead !== 'function') return [];
    const stored = `md:${key}`;
    /* Checked before anything is read: the pull loop calls this for every
       document that arrives, and most of them are not writing at all. */
    if (!mdRecordDocIsHome(stored)) return [];

    const held = mdRecordDocRows(stored, g.mdRead(stored, null));
    const refs = Object.keys(held);
    if (!refs.length) return [];

    const arriving = mdRecordDocRows(stored, value);
    const out: string[] = [];
    for (const ref of refs) {
      const was = mdRecordDocText(held[ref]);
      /* Nothing written is nothing to lose, and text the change leaves alone is
         text the record still says. Either one would put a row in a version
         list that nobody could tell apart from the record in front of them. */
      if (!was || was === mdRecordDocText(arriving[ref])) continue;
      const kept = await mdVersionPut(ref, was, { at: displacedAt(arriving[ref]), what: 'text' });
      if (kept) out.push(kept);
    }
    return out;
  }

  /** Keep what this device is about to OVERRIDE. The mirror of the function
      above, and it exists because the conflict policy has two sides.

      When both devices changed the same unmergeable document, this one keeps
      what it is holding and overrides the server's copy — later-informed wins.
      That is the right call for the person at this keyboard, and it is also the
      only moment in the whole sync where another device's writing is about to
      stop existing anywhere. `mdVersionSnapshotIncoming` cannot help: it keeps
      the LOCAL text, which is the half that survives.

      So the arriving text is archived instead, under the same record refs, and
      turns up in that record's Versions list beside every other displaced
      draft. "Nothing is lost silently" is the promise; this is the line that
      keeps it. Answers the keys it kept. */
  async function mdVersionSnapshotDisplaced(key: unknown, value: unknown): Promise<string[]> {
    if (typeof key !== 'string' || !key || typeof g.mdRead !== 'function') return [];
    const stored = `md:${key}`;
    if (!mdRecordDocIsHome(stored)) return [];

    const arriving = mdRecordDocRows(stored, value);
    const refs = Object.keys(arriving);
    if (!refs.length) return [];

    const held = mdRecordDocRows(stored, g.mdRead(stored, null));
    const out: string[] = [];
    for (const ref of refs) {
      const theirs = mdRecordDocText(arriving[ref]);
      /* Text this device already has is not a version of anything — and an
         empty arrival displaces nothing, exactly as on the other side. */
      if (!theirs || theirs === mdRecordDocText(held[ref])) continue;
      const kept = await mdVersionPut(ref, theirs, { at: displacedAt(arriving[ref]), what: 'text' });
      if (kept) out.push(kept);
    }
    return out;
  }

  /** Tests and account switching — module state, and a held handle to another
      account's database is the one way this file could leak across a seam. */
  function mdVersionsForget(): void {
    dbPromise = null;
    dbName = null;
  }

  Object.assign(g, {
    MD_HISTORY_PREFIX, MD_HISTORY_LOG_MAX, MD_HISTORY_INLINE_MAX,
    mdHistoryLogKey, mdHistoryLogAppend, mdHistoryLogRead, mdHistoryLogRemoveFor,
    mdHistoryLogSyncDocs, mdHistoryLogHeldKeys, mdHistoryLogSanitiseSyncDoc,
    mdHistoryLogApplyDoc, mdHistoryLogMergesDoc,
    mdRecordDocIsHome, mdRecordDocRows, mdRecordDocText,
    mdVersionsAvailable, mdVersionPut, mdVersionKeep, mdVersionGet, mdVersionList,
    mdVersionRemoveFor, mdVersionSnapshotIncoming, mdVersionSnapshotDisplaced,
    mdVersionHealth, mdVersionsForget,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_HISTORY_PREFIX, MD_HISTORY_LOG_MAX, MD_HISTORY_INLINE_MAX,
      mdHistoryLogKey, mdHistoryLogAppend, mdHistoryLogRead, mdHistoryLogRemoveFor,
      mdHistoryLogSyncDocs, mdHistoryLogHeldKeys, mdHistoryLogSanitiseSyncDoc,
      mdHistoryLogApplyDoc, mdHistoryLogMergesDoc,
      mdRecordDocIsHome, mdRecordDocRows, mdRecordDocText,
      mdVersionsAvailable, mdVersionPut, mdVersionKeep, mdVersionGet, mdVersionList,
      mdVersionRemoveFor, mdVersionSnapshotIncoming, mdVersionSnapshotDisplaced,
    mdVersionHealth, mdVersionsForget,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
